// ./src/components/PoiManager.tsx
import React, { useEffect, useState, useMemo } from 'react';

import {
  Box, Typography, IconButton, Button, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Checkbox, Toolbar,
  TablePagination, TableSortLabel, TextField
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { api } from "../utils/api.ts";

import Icons from './Icons.tsx';
import PoiEditor from './PoiEditor.tsx';

interface PoiManagerProps {
  user: User;
  displayedUser: User;
  pois: POI[];
  setPois: () => void;
}

const PoiManager: React.FC<PoiManagerProps> = ({ user, displayedUser, pois, setPois }) => {
  const [selected, setSelected] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<keyof POI>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filter, setFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPoi, setEditingPoi] = useState<POI | null>(null);

  useEffect(() => {
    const params = user.admin ? { user_id: displayedUser.id } : undefined;
    api.get("/api/pois", { params }).then(res => setPois(res.data));
  }, [user.admin, displayedUser]);

  const filtered = useMemo(() => {
    return pois.filter(p =>
      p.name.toLowerCase().includes(filter.toLowerCase()) ||
      p.description?.toLowerCase().includes(filter.toLowerCase())
    );
  }, [pois, filter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortBy, sortOrder]);

  const handleDelete = async () => {
    await api.post("/api/pois/delete", { ids: selected });
    setPois((prev: any) => prev.filter((p: any) => !selected.includes(p.id)));
    setSelected([]);
  };

  const handleSavePoi = (newPoi: POI) => {
    const updated = pois.some(p => p.id === newPoi.id)
      ? pois.map(p => (p.id === newPoi.id ? newPoi : p))
      : [...pois, newPoi];
    setPois(updated);
    setOpenDialog(false);
    setEditingPoi(null);
  };

  return (
    <Box sx={{
      height: '100vh',
      width: 'calc(100vw - 56px)',
      position: 'absolute',
      zIndex: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}> 
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>     
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <TextField
            label="Search"
            size="small"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          <Box>
            {selected.length > 0 && (
              <Button
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                sx={{ mr: 2 }}
                color="error"
              >
                Delete
              </Button>
            )}
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => {
                setEditingPoi(null);
                setOpenDialog(true);
              }}
            >
              New POI
            </Button>
          </Box>
        </Toolbar>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell padding="checkbox" />
                {['name', 'latitude', 'longitude', 'radius', 'description', 'osm'].map(col => (
                  <TableCell key={col}>
                    <TableSortLabel
                      active={sortBy === col}
                      direction={sortOrder}
                      onClick={() => {
                        if (sortBy === col) {
                          setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
                        } else {
                          setSortBy(col as keyof POI);
                          setSortOrder('asc');
                        }
                      }}
                    >
                      {col.charAt(0).toUpperCase() + col.slice(1)}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map(poi => (
                <TableRow
                  key={poi.id}
                  hover
                  selected={selected.includes(poi.id)}
                  onClick={() => {
                    setEditingPoi(poi);
                    setOpenDialog(true);
                  }}
                >
                  <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.includes(poi.id)}
                      onChange={() => {
                        setSelected(prev =>
                          prev.includes(poi.id)
                            ? prev.filter(id => id !== poi.id)
                            : [...prev, poi.id]
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {poi.icon && (
                      <Box style={{
                        color: poi.color
                      }}>
                        {Icons.pois.inline[poi.icon.toLowerCase()]}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{poi.name}</TableCell>
                  <TableCell>{poi.latitude}</TableCell>
                  <TableCell>{poi.longitude}</TableCell>
                  <TableCell>{poi.radius}</TableCell>
                  <TableCell>{poi.description}</TableCell>
                  <TableCell>{poi.osm_reference}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <TablePagination
        count={sorted.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          '& .MuiTablePagination-toolbar': {
            display: 'flex',
            alignItems: 'center', // fix vertical alignment
            justifyContent: 'space-between',
            minHeight: '48px' // to keep it compact and consistent
          },
          '& .MuiTablePagination-selectLabel': {
            margin: 0,
            alignSelf: 'center',
          },
          '& .MuiTablePagination-displayedRows': {
            margin: 0,
            alignSelf: 'center',
          },
          '& .MuiTablePagination-actions': {
            alignSelf: 'center',
          }
        }}
      />

      <PoiEditor
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        displayedUser={displayedUser}
        existingPoi={editingPoi}
        onSave={handleSavePoi}
      />
    </Box>
  );
};

export default PoiManager;
